import {
  Dropdown,
  Option,
  OptionGroup,
  Text,
  TextButton,
  TextField,
  TextWithLabel,
} from '@joor/design-system'
import { useSelector } from 'react-redux'

import { OrderStatus } from '__generated__/atlas-types'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'
import { isAccountTypeBrand as isAccountTypeBrandSelector } from 'store/currentUser/selectors'

import { useInputFieldKeyHandlers } from '../../EditAddressesModal/hooks/useInputFieldKeyHandlers'
import {
  HandleShippingChange,
  ShippingFormikErrors,
} from '../../EditAddressesModal/hooks/useShippingForm'
import { CreateAddressLink } from '../CreateAddressLink/CreateAddressButton'
import {
  ClearFormButtonWrapper,
  Column,
  FieldsContainer,
  FormContainer,
  FormHeaderHolder,
  TextFieldGroup,
} from '../forms.ui'
import { UNITED_STATES_ID } from '../forms.utils'
import {
  FieldIds,
  ShippingAddressFormValues,
} from './shippingAddressForm.types'
import { messages } from 'features/Orders/Review/components/Overview/components/AddressesWidget/constants/messages'
import { AddressReviewPageIds } from 'features/Orders/Review/review.ids'
import { DropdownCallbacks } from 'features/Orders/Review/utils/formik.utils'

export enum ShippingAddressFormTestIds {
  SelectAddressDropdown = 'shipping-form-select-address-dropdown',
  CountryDropdown = 'shipping-form-country-dropdown',
  DoorDropdown = 'shipping-form-door-dropdown',
  ShippingMethodDropdown = 'shipping-form-shipping-method-dropdown',
}

const TOOLTIP_INFO_TEXT = 'You are restricted from editing this information.'

export type Props = {
  onSelectAddress: ({ selected }: { selected: Option }) => void
  addressesOptionGroup: OptionGroup[]
  aggregatedOptions: Option[]
  disableEditCompany?: boolean
  formId: string
  showShippingCode?: boolean
  doorOptions?: Option[]
  shippingMethodOptions?: Option[]
  orderStatus?: OrderStatus
  countryOptions?: Option[]
  shippingCountryHandlers: DropdownCallbacks
  shippingDoorHandlers: DropdownCallbacks
  shippingMethodHandlers: DropdownCallbacks
  values: ShippingAddressFormValues
  errors: ShippingFormikErrors
  resetForm: () => void
  handleChange: HandleShippingChange
  retailerId?: string
  isRetailerRestrictedFromCreating?: boolean
  companyNameLabel: string
  readonly?: boolean
}

export const ShippingAddressForm = ({
  onSelectAddress,
  addressesOptionGroup = [],
  aggregatedOptions = [],
  disableEditCompany,
  formId,
  showShippingCode = false,
  doorOptions = [],
  shippingMethodOptions = [],
  orderStatus,
  countryOptions,
  shippingCountryHandlers,
  shippingDoorHandlers,
  shippingMethodHandlers,
  values,
  errors,
  resetForm,
  handleChange,
  retailerId,
  isRetailerRestrictedFromCreating = false,
  companyNameLabel,
  readonly,
}: Props) => {
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)

  const { handleKeyDown } = useInputFieldKeyHandlers()

  return (
    <FormContainer id={formId}>
      <FormHeaderHolder>
        <div className="address-select">
          <Dropdown
            testId={ShippingAddressFormTestIds.SelectAddressDropdown}
            id={FieldIds.AddressOption}
            label={messages.selectAddress}
            value={values[FieldIds.AddressOption]}
            options={addressesOptionGroup}
            placeholder={EMPTY_CELL_CONTENT}
            onSelect={onSelectAddress}
            errorMessage={errors[FieldIds.AddressOption]}
            isDisabled={!aggregatedOptions.length}
            isRequired
          />
        </div>
        {!readonly && (
          <CreateAddressLink
            retailerId={retailerId}
            isRetailerRestricted={isRetailerRestrictedFromCreating}
            createAddressId={AddressReviewPageIds.CreateAddressButtonShippingId}
          />
        )}
      </FormHeaderHolder>
      {!readonly && (
        <ClearFormButtonWrapper>
          <TextButton
            onClick={(e) => {
              e.preventDefault()
              resetForm()
            }}
          >
            {messages.clearAllFields}
          </TextButton>
        </ClearFormButtonWrapper>
      )}
      {!readonly ? (
        <FieldsContainer>
          <Column>
            {disableEditCompany ? (
              <TextWithLabel label={companyNameLabel}>
                <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                  {values[FieldIds.CompanyName]}
                </Text>
              </TextWithLabel>
            ) : (
              <TextField
                id={FieldIds.CompanyName}
                label={companyNameLabel}
                value={values[FieldIds.CompanyName]}
                defaultValue={
                  values[FieldIds.CompanyName] ?? EMPTY_CELL_CONTENT
                }
                onChange={handleChange}
                placeholder={EMPTY_CELL_CONTENT}
                errorMessage={errors[FieldIds.CompanyName]}
                onKeyDown={handleKeyDown}
                isRequired={!disableEditCompany}
              />
            )}
            <TextField
              id={FieldIds.Address1}
              label={messages.address1}
              value={values[FieldIds.Address1]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              errorMessage={errors[FieldIds.Address1]}
              onKeyDown={handleKeyDown}
              isRequired
            />
            <TextField
              id={FieldIds.City}
              label={messages.city}
              value={values[FieldIds.City]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              errorMessage={errors[FieldIds.City]}
              onKeyDown={handleKeyDown}
              isRequired
            />
            <Dropdown
              testId={ShippingAddressFormTestIds.CountryDropdown}
              id={FieldIds.Country}
              label={messages.country}
              value={values[FieldIds.Country]}
              options={countryOptions}
              placeholder={EMPTY_CELL_CONTENT}
              errorMessage={errors[FieldIds.Country]}
              isDisabled={!countryOptions?.length}
              isRequired
              menuPlacement="top"
              {...shippingCountryHandlers}
            />
            <TextField
              id={FieldIds.Email}
              label={messages.email}
              value={values[FieldIds.Email]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              errorMessage={errors[FieldIds.Email]}
              onKeyDown={handleKeyDown}
              isRequired
            />
            <Dropdown
              id={FieldIds.Door}
              testId={ShippingAddressFormTestIds.DoorDropdown}
              label={messages.door}
              options={doorOptions}
              placeholder={EMPTY_CELL_CONTENT}
              menuPlacement="top"
              value={values[FieldIds.Door]}
              isDisabled={!isAccountTypeBrand}
              {...shippingDoorHandlers}
            />
          </Column>
          <Column>
            <TextFieldGroup>
              {showShippingCode && (
                <TextWithLabel
                  label="Shipping Code"
                  caption={messages.getShippingCodeCaption(isAccountTypeBrand)}
                >
                  <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                    {values[FieldIds.ShippingCode] ?? EMPTY_CELL_CONTENT}
                  </Text>
                </TextWithLabel>
              )}
              <Dropdown
                id={FieldIds.ShippingMethod}
                label={messages.shippingMethod}
                testId={ShippingAddressFormTestIds.ShippingMethodDropdown}
                placeholder={EMPTY_CELL_CONTENT}
                options={shippingMethodOptions}
                value={values[FieldIds.ShippingMethod]}
                isDisabled={
                  !isAccountTypeBrand && orderStatus !== OrderStatus.NOTES
                }
                {...shippingMethodHandlers}
              />
            </TextFieldGroup>
            <TextField
              id={FieldIds.Address2}
              label={messages.address2}
              value={values[FieldIds.Address2]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              onKeyDown={handleKeyDown}
            />
            <TextField
              id={FieldIds.Territory}
              label={
                values[FieldIds.Country]?.value === UNITED_STATES_ID
                  ? messages.state
                  : messages.territory
              }
              value={values[FieldIds.Territory]}
              onChange={handleChange}
              errorMessage={errors[FieldIds.Territory]}
              placeholder={EMPTY_CELL_CONTENT}
              onKeyDown={handleKeyDown}
            />
            <TextField
              id={FieldIds.PostalCode}
              label={
                values[FieldIds.Country]?.value === UNITED_STATES_ID
                  ? messages.zipCode
                  : messages.postalCode
              }
              value={values[FieldIds.PostalCode]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              onKeyDown={handleKeyDown}
            />
            <TextField
              id={FieldIds.Phone}
              label={messages.phone}
              value={values[FieldIds.Phone]}
              onChange={handleChange}
              maxLength={20}
              placeholder="Phone"
              errorMessage={errors[FieldIds.Phone]}
              onKeyDown={handleKeyDown}
            />
            {orderStatus === OrderStatus.SHIPPED && (
              <TextField
                id={FieldIds.TrackingNumber}
                label={messages.trackingNumber}
                value={values[FieldIds.TrackingNumber]}
                onChange={handleChange}
                max={100}
                type="alphanumeric"
                placeholder={EMPTY_CELL_CONTENT}
                onKeyDown={handleKeyDown}
                disabled={!isAccountTypeBrand}
              />
            )}
          </Column>
        </FieldsContainer>
      ) : (
        <FieldsContainer>
          <Column>
            <TextWithLabel label={companyNameLabel} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.CompanyName] ?? EMPTY_CELL_CONTENT}
              </Text>
            </TextWithLabel>
            <TextWithLabel
              label={messages.address1}
              caption={TOOLTIP_INFO_TEXT}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Address1]}
              </Text>
            </TextWithLabel>
            <TextWithLabel label={messages.city} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.City]}
              </Text>
            </TextWithLabel>
            <TextWithLabel label={messages.country} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Country]?.label}
              </Text>
            </TextWithLabel>
            <TextWithLabel label={messages.email} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Email]}
              </Text>
            </TextWithLabel>
            <Dropdown
              id={FieldIds.Door}
              testId={ShippingAddressFormTestIds.DoorDropdown}
              label={messages.door}
              options={doorOptions}
              placeholder={EMPTY_CELL_CONTENT}
              menuPlacement="top"
              value={values[FieldIds.Door]}
              isDisabled={!isAccountTypeBrand}
              {...shippingDoorHandlers}
            />
          </Column>
          <Column>
            <TextFieldGroup>
              {showShippingCode && (
                <TextWithLabel
                  label="Shipping Code"
                  caption={TOOLTIP_INFO_TEXT}
                >
                  <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                    {values[FieldIds.ShippingCode] ?? EMPTY_CELL_CONTENT}
                  </Text>
                </TextWithLabel>
              )}
              <Dropdown
                id={FieldIds.ShippingMethod}
                label={messages.shippingMethod}
                testId={ShippingAddressFormTestIds.ShippingMethodDropdown}
                placeholder={EMPTY_CELL_CONTENT}
                options={shippingMethodOptions}
                value={values[FieldIds.ShippingMethod]}
                isDisabled={
                  !isAccountTypeBrand && orderStatus !== OrderStatus.NOTES
                }
                {...shippingMethodHandlers}
              />
            </TextFieldGroup>
            <TextWithLabel
              label={messages.address2}
              caption={TOOLTIP_INFO_TEXT}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Address2]}
              </Text>
            </TextWithLabel>
            <TextWithLabel
              label={
                values[FieldIds.Country]?.value === UNITED_STATES_ID
                  ? messages.state
                  : messages.territory
              }
              caption={TOOLTIP_INFO_TEXT}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Territory]}
              </Text>
            </TextWithLabel>
            <TextWithLabel
              label={
                values[FieldIds.Country]?.value === UNITED_STATES_ID
                  ? messages.zipCode
                  : messages.postalCode
              }
              caption={TOOLTIP_INFO_TEXT}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.PostalCode]}
              </Text>
            </TextWithLabel>
            <TextWithLabel label={messages.phone} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Phone]}
              </Text>
            </TextWithLabel>
            {orderStatus === OrderStatus.SHIPPED && (
              <TextWithLabel
                label={messages.trackingNumber}
                caption={TOOLTIP_INFO_TEXT}
              >
                <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                  {values[FieldIds.TrackingNumber]}
                </Text>
              </TextWithLabel>
            )}
          </Column>
        </FieldsContainer>
      )}
    </FormContainer>
  )
}
